import { AdPositionElements, insertBanners } from '@schibsted-nmp/advertising-companion';
import { IconInfo24 } from '@warp-ds/icons/react';
import { Box } from '@warp-ds/react/components/box';
import { Pagination } from '@warp-ds/react/components/pagination';
import { type ReactNode, useEffect, useMemo } from 'react';
import { useExperiment } from '@/hooks/experiments';
import { ApiActionStatus, PRICE_MIGHT_VARY_WARNING } from '@/interfaces';
import { trackResultPage } from '@/lib/utils/tracking';
import { EmptyWarning } from '../emptyWarning/EmptyWarning';
import { ResultListItem } from './components/ResultListItem';
import { ResultListItemClickable } from './components/ResultListItemClickable';
import { useResultsNavigator } from './hooks/useResultsNavigator';

type ResultListProps = {
    className?: string;
    includeBanners?: boolean;
};

export const ResultList = ({ className = '', includeBanners = true }: ResultListProps) => {
    const { results, filters, currentPage, totalPages, createHref, handleChange, resetAndRefetch } = useResultsNavigator();
    const shouldShowPriceWarning = filters.agesOfChildren?.some((age) => age > 12 || age < 2);
    const [bannerPositions, bannerElements] = AdPositionElements.use({ layoutType: 'list' });
    const experiment = useExperiment();
    const variant = experiment?.['travel-prepackage-clickable_result_item'];

    const ItemComponent = variant === 'treatment' ? ResultListItemClickable : ResultListItem;

    const items = useMemo(
        () => results.offers.map((o) => <ItemComponent key={o.offerId} offer={o} filters={filters} variant={variant} />),
        [results.offers, filters, variant],
    );

    const itemsWithBanners = useMemo(
        () =>
            includeBanners
                ? insertBanners({
                      adItems: items,
                      bannerPositions: bannerPositions as number[],
                      bannerElements: bannerElements as ReactNode[],
                  })
                : items,
        [items, bannerPositions, bannerElements, includeBanners],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger new tracking when the list of offers changes
    useEffect(() => {
        trackResultPage(results, filters);
    }, [results.offers]);

    return (
        <section
            data-testid="resultList"
            className={`flex flex-col gap-16 ${className}`}
            aria-busy={results.status === ApiActionStatus.PENDING || undefined}>
            <h2 className="sr-only">Resultater</h2>
            {results.offers.length === 0 ? (
                <EmptyWarning onReset={resetAndRefetch} testId="emptyWarning-result" />
            ) : (
                <>
                    {shouldShowPriceWarning && (
                        <Box info>
                            <div className="flex gap-x-16">
                                <IconInfo24 className="s-text-link mt-2" />
                                <p className="mb-0">{PRICE_MIGHT_VARY_WARNING}</p>
                            </div>
                        </Box>
                    )}
                    {itemsWithBanners}
                    {totalPages > 1 && (
                        <Pagination createHref={createHref} currentPage={currentPage} lastPage={totalPages} onChange={handleChange} />
                    )}
                </>
            )}
        </section>
    );
};
